import { z } from 'zod';

/**
 * Zod schema for validating scene JSON export/import (Parametric Animation Refactor)
 *
 * Updated to use new Tool interface:
 * - targetRotation: degrees (constrained by motor limits)
 * - easing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'
 *
 * Removed old properties:
 * - speed
 * - intensity
 * - parameters
 *
 * Added puppet support:
 * - puppets: array of puppet definitions with nested tools
 * - Backward compatible: puppets field is optional
 *
 * Added groups support (Task Group 7):
 * - groups: array of reusable tool groups with relative timing
 * - Backward compatible: groups field is optional
 */

/**
 * Body part validation
 */
const bodyPartSchema = z.enum([
  'head',
  'body',
  'leftArm',
  'rightArm',
  'leftLeg',
  'rightLeg',
  'control',
]);

/**
 * Easing function validation
 */
const easingFunctionSchema = z.enum([
  'linear',
  'ease-in',
  'ease-out',
  'ease-in-out',
]);

/**
 * Camera position schema
 */
const cameraPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

/**
 * HTTP endpoints schema for tool hardware control
 */
const httpEndpointsSchema = z
  .object({
    start: z.string().optional(),
    stop: z.string().optional(),
  })
  .optional();

/**
 * Motor positions schema - rotation angles for each body part
 */
const motorPositionsSchema = z.object({
  head: z.number(),
  body: z.number(),
  leftArm: z.number(),
  rightArm: z.number(),
  leftLeg: z.number(),
  rightLeg: z.number(),
});

/**
 * Motor config schema - hardware configuration for a single motor
 */
const motorConfigSchema = z.object({
  id: z.string().optional(),
  bodyPart: bodyPartSchema,
  minAngle: z.number(),
  maxAngle: z.number(),
  axis: z.enum(['x', 'y', 'z']).optional(),
  boneOffset: z.number().optional(),
  direction: z.number().optional(),
  xOffset: z.number().optional(),
  yOffset: z.number().optional(),
  zOffset: z.number().optional(),
});

/**
 * Puppet tool schema - defines a tool owned by a puppet
 *
 * Contains all ToolDefinition fields with validation.
 * Used for serializing/deserializing puppet tools in scene export/import.
 */
export const puppetToolSchema = z.object({
  id: z.string().min(1, 'Tool ID is required'),
  name: z.string().min(1, 'Tool name is required'),
  bodyPart: bodyPartSchema,
  type: z.literal('prebuilt').optional().default('prebuilt'),
  defaultDuration: z
    .number()
    .min(0.1, 'Default duration must be at least 0.1 seconds')
    .max(10, 'Default duration cannot exceed 10 seconds'),
  defaultEasing: easingFunctionSchema,
  minDegrees: z
    .number()
    .min(-180, 'Minimum degrees cannot be less than -180')
    .max(180, 'Minimum degrees cannot exceed 180'),
  maxDegrees: z
    .number()
    .min(-180, 'Maximum degrees cannot be less than -180')
    .max(180, 'Maximum degrees cannot exceed 180'),
  dockingPosition: z.number().min(-180).max(180).optional().default(0),
  startingPosition: z.number().min(-180).max(180).optional().default(0),
  color: z.string().min(1, 'Tool color is required'),
  isPrebuilt: z.boolean(),
  icon: z.string().optional(),
  httpEndpoints: httpEndpointsSchema,
  axis: z.enum(['x', 'y', 'z']).optional().default('x'),
});

/**
 * Puppet schema - defines a puppet with its owned tools
 *
 * A puppet represents a character with its own set of animation tools.
 */
export const puppetSchema = z.object({
  id: z.string().min(1, 'Puppet ID is required'),
  name: z
    .string()
    .min(1, 'Puppet name is required')
    .max(50, 'Puppet name cannot exceed 50 characters'),
  modelPath: z.string().optional().default(''),
  description: z
    .string()
    .max(500, 'Description cannot exceed 500 characters')
    .optional(),
  url: z.string().url('Invalid URL format').optional().or(z.literal('')),
  tools: z.array(puppetToolSchema),
  // Motor configuration (optional for backward compatibility)
  motors: z.array(motorConfigSchema).optional(),
  startingPosition: motorPositionsSchema.optional(),
  dockingPosition: motorPositionsSchema.optional(),
  movementSpeed: z.number().optional(),
});

const resetTargetPositionSchema = z.enum(['start', 'dock']).optional();

/**
 * Tool schema with all required fields and validation
 * Updated for parametric animation system
 */
const toolSchema = z.object({
  id: z.string().min(1, 'Tool ID is required'),
  toolId: z.string().min(1, 'Tool ID reference is required'),
  name: z.string().min(1, 'Tool name is required'),
  bodyPart: bodyPartSchema,
  startTime: z
    .number()
    .min(0, 'Start time must be non-negative')
    .max(3600, 'Start time cannot exceed 1 hour'),
  duration: z
    .number()
    .min(0.1, 'Duration must be at least 0.1 seconds')
    .max(10, 'Duration cannot exceed 10 seconds'),
  trackNumber: z
    .number()
    .int('Track number must be an integer')
    .min(1, 'Track number must be at least 1')
    .max(100, 'Track number cannot exceed 100'),
  targetRotation: z
    .number()
    .min(-180, 'Target rotation must be at least -180 degrees')
    .max(180, 'Target rotation cannot exceed 180 degrees'),
  easing: easingFunctionSchema,
  targetPosition: resetTargetPositionSchema,
  puppetId: z.string().optional(),
});

/**
 * Timeline schema containing duration and tools
 */
const timelineSchema = z.object({
  duration: z
    .number()
    .min(10, 'Timeline duration must be at least 10 seconds')
    .max(3600, 'Timeline duration cannot exceed 1 hour'),
  tools: z.array(toolSchema).max(1000, 'Cannot have more than 1000 tools'),
});

/**
 * Tab schema with timeline data
 */
const tabSchema = z.object({
  id: z.string().min(1, 'Tab ID is required'),
  name: z
    .string()
    .min(1, 'Tab name is required')
    .max(30, 'Tab name cannot exceed 30 characters'),
  order: z
    .number()
    .int('Order must be an integer')
    .min(0, 'Order must be non-negative')
    .max(9, 'Order cannot exceed 9 (max 10 tabs)'),
  cameraPosition: cameraPositionSchema,
  modelPath: z.string().optional(),
  timeline: timelineSchema,
});

/**
 * Scene metadata schema
 */
const metadataSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
});

/**
 * Group tool schema - defines a tool within a group with relative timing
 *
 * Contains tool configuration with relative start time and track number
 * for positioning when the group is unpacked onto the timeline.
 */
export const groupToolSchema = z.object({
  toolId: z.string().min(1, 'Tool ID reference is required'),
  bodyPart: bodyPartSchema,
  duration: z
    .number()
    .min(0.1, 'Duration must be at least 0.1 seconds')
    .max(10, 'Duration cannot exceed 10 seconds'),
  targetRotation: z
    .number()
    .min(-180, 'Target rotation must be at least -180 degrees')
    .max(180, 'Target rotation cannot exceed 180 degrees'),
  easing: easingFunctionSchema,
  /** Time offset relative to earliest tool (earliest = 0) */
  relativeStartTime: z
    .number()
    .min(0, 'Relative start time must be non-negative'),
  /** Track offset relative to first track used (1-indexed) */
  relativeTrackNumber: z
    .number()
    .int('Relative track number must be an integer')
    .min(1, 'Relative track number must be at least 1'),
  /** Target position for Reset Position tool only */
  targetPosition: resetTargetPositionSchema,
  /** Optional puppet ID for multi-puppet support */
  puppetId: z.string().optional(),
});

/**
 * Group schema - defines a reusable tool group
 *
 * Groups store tool snapshots with relative timing so they can be
 * unpacked at any position on the timeline while preserving their
 * internal timing relationships.
 */
export const groupSchema = z.object({
  id: z.string().min(1, 'Group ID is required'),
  name: z
    .string()
    .min(1, 'Group name is required')
    .max(50, 'Group name cannot exceed 50 characters'),
  tools: z
    .array(groupToolSchema)
    .min(1, 'Group must contain at least one tool'),
  trackCount: z.number().int().min(1).optional(),
});

/**
 * Placed group schema - a group instance placed on the timeline
 *
 * References a group definition by ID and records its position
 * on the timeline (startTime, trackNumber).
 */
export const placedGroupSchema = z.object({
  id: z.string().min(1, 'Placed group ID is required'),
  groupId: z.string().min(1, 'Group ID reference is required'),
  trackNumber: z
    .number()
    .int('Track number must be an integer')
    .min(1, 'Track number must be at least 1'),
  startTime: z.number().min(0, 'Start time must be non-negative'),
  puppetId: z.string().optional(),
});

/**
 * Complete scene schema for export/import
 *
 * The puppets field is optional for backward compatibility with older scene files.
 * When importing a scene without puppets, the Starter Puppet is kept or created.
 *
 * The groups field is optional for backward compatibility with older scene files.
 * When importing a scene without groups, the groups array is set to empty.
 */
export const sceneSchema = z.object({
  version: z.string().min(1, 'Version is required'),
  createdAt: z.string().datetime('Invalid ISO timestamp'),
  metadata: metadataSchema.optional(),
  puppetModel: z.string().optional(),
  tabs: z
    .array(tabSchema)
    .min(1, 'At least one tab is required')
    .max(10, 'Cannot have more than 10 tabs'),
  puppets: z
    .array(puppetSchema)
    .min(1, 'At least one puppet is required')
    .optional(),
  /** Reusable tool groups (optional for backward compatibility) */
  groups: z.array(groupSchema).optional(),
  /** Placed group instances on the timeline (optional for backward compatibility) */
  placedGroups: z.array(placedGroupSchema).optional(),
});

/**
 * Type inference from schema
 */
export type SceneExport = z.infer<typeof sceneSchema>;
export type TabExport = z.infer<typeof tabSchema>;
export type ToolExport = z.infer<typeof toolSchema>;
export type PuppetExport = z.infer<typeof puppetSchema>;
export type PuppetToolExport = z.infer<typeof puppetToolSchema>;
export type GroupExport = z.infer<typeof groupSchema>;
export type GroupToolExport = z.infer<typeof groupToolSchema>;
export type PlacedGroupExport = z.infer<typeof placedGroupSchema>;

/**
 * Validation result type
 */
export interface ValidationError {
  path: string;
  message: string;
  code: string;
  received?: unknown;
  expected?: string;
  suggestion?: string;
}

/**
 * Format Zod validation errors into user-friendly format
 */
export function formatValidationErrors(error: z.ZodError): ValidationError[] {
  return error.issues.map((issue) => {
    const path = issue.path.join('.');
    const message = issue.message;
    const code = issue.code;

    // Generate helpful suggestions based on error type
    let suggestion: string | undefined;
    let expected: string | undefined;

    // Use any to access properties that may not be typed correctly in Zod v4
    const issueAny = issue as any;

    switch (code) {
      case 'invalid_type':
        if (issueAny.expected) {
          expected = String(issueAny.expected);
          suggestion = `Ensure this field is of type ${issueAny.expected}`;
        }
        break;
      case 'too_small':
        if (issueAny.minimum !== undefined) {
          suggestion = `Minimum value is ${issueAny.minimum}`;
        }
        break;
      case 'too_big':
        if (issueAny.maximum !== undefined) {
          suggestion = `Maximum value is ${issueAny.maximum}`;
        }
        break;
      case 'invalid_enum_value':
        suggestion = `Valid values are: ${issueAny.options?.join(', ') || 'check schema'}`;
        break;
      default:
        // For other error types, just use the message
        suggestion = 'Please check the value and try again';
        break;
    }

    // Add puppet-specific suggestions
    if (path.startsWith('puppets')) {
      if (path.includes('.tools')) {
        suggestion = `Check the tool configuration in the puppet. ${suggestion}`;
      } else if (path.includes('.name')) {
        suggestion = `Puppet names must be 1-50 characters. ${suggestion}`;
      } else if (path.includes('.description')) {
        suggestion = `Descriptions are limited to 500 characters. ${suggestion}`;
      }
    }

    // Add group-specific suggestions
    if (path.startsWith('groups')) {
      if (path.includes('.tools')) {
        suggestion = `Check the tool configuration in the group. ${suggestion}`;
      } else if (path.includes('.name')) {
        suggestion = `Group names must be 1-50 characters. ${suggestion}`;
      }
    }

    return {
      path,
      message,
      code,
      received: issueAny.received,
      expected,
      suggestion,
    };
  });
}
